import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import { RefreshTokenError } from '../types/auth.types';
import { enVisorPublico } from '../utils/public-route.util';
import { guardarRutaDestino } from '../utils/return-url.util';

// Arreglo de rutas/URLs donde se omite la verificación
const EXCLUDED_URLS = ['/auth/refresh', '/auth/login', '/viewer/'];

// URLs públicas que requieren token de prueba
const PUBLIC_VIEWER_URLS = ['/public/documents'];

// Token de prueba para el visor público
const TEST_BEARER_TOKEN = 'public';

/**
 * Cabecera con la que una petición declara que su 403 no debe notificarse al
 * usuario. Se usa en peticiones complementarias, cuyo fallo se degrada en
 * silencio porque la pantalla se muestra igual.
 */
export const SILENT_FORBIDDEN_HEADER = 'X-Silent-Forbidden';

/**
 * Ventana mínima entre avisos de problema de conexión, en milisegundos.
 * Varias peticiones concurrentes comparten el mismo refresh; sin esta ventana
 * un solo corte de red produciría una ráfaga de toasts idénticos.
 */
const CONNECTION_TOAST_COOLDOWN_MS = 10_000;

/**
 * Interceptor que se encarga de:
 * 1. Verificar si el token está a punto de expirar o expiró antes de hacer la petición;
 *    si es así, refresca el token.
 * 2. Agregar el token (Bearer) en las cabeceras.
 * 3. Manejar errores 401, si ocurrieran.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);

  /**
   * Marca de tiempo del último aviso de conexión emitido, para no repetirlo
   * en ráfaga cuando varias peticiones fallan por el mismo corte de red.
   */
  private lastConnectionToastAt = 0;

  /**
   * Método principal que intercepta todas las peticiones HTTP
   * @param request Petición HTTP original
   * @param next Siguiente manejador en la cadena de interceptores
   * @returns Observable<HttpEvent<any>>
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isProtectedApiRequest(request)) {
      return next.handle(request);
    }

    // Verificar si la URL debe excluirse
    if (this.isExcludedUrl(request)) {
      // No agregamos token ni hacemos verificación de expiración
      return next.handle(request);
    }

    // Verificar si es una URL del visor público que requiere token de prueba
    if (this.isPublicViewerUrl(request)) {
      const authReq = request.clone({
        headers: request.headers.set(
          'Authorization',
          `Bearer ${TEST_BEARER_TOKEN}`,
        ),
      });
      return next.handle(authReq);
    }

    // Obtenemos el token (Access Token JWT) del localStorage (o de donde lo guardes)
    let token = this.authService.getAccessToken();

    // Si no hay token, redirigimos a login y enviamos la petición sin cabecera Authorization.
    // Desde el visor público no se redirige: es una pantalla sin sesión y el
    // salto al login sería un falso positivo.
    if (!token) {
      if (!enVisorPublico()) {
        this.router.navigate(['/auth/login']);
      }
      return next.handle(request);
    }

    // Verificamos si el token está expirado o por expirar
    const isExpiringSoon = this.authService.isTokenExpiringSoon(token);
    if (isExpiringSoon) {
      // Refrescamos antes de enviar la petición y compartimos el resultado
      return this.authService.handleRefreshToken(token).pipe(
        switchMap((newToken: string) => {
          // Guardamos el nuevo token en localStorage (o donde proceda)
          this.authService.setAccessToken(newToken);
          token = newToken; // actualizamos la variable local

          // Ahora sí, clonamos la petición con el token nuevo
          const authReq = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`),
          });
          return next.handle(authReq);
        }),
        catchError((err) => this.handleRefreshFailure(err)),
      );
    }

    // Si NO está próximo a expirar, simplemente enviamos la petición con el token actual
    const authReq = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });

    // Continuamos con el flujo de la petición
    return next.handle(authReq).pipe(
      catchError((error) => {
        // Si el status es 401 (por ej., token inválido o error del backend)
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Reintento único: intentamos refrescar y reemitir la petición una vez
          const alreadyRetried = request.headers.has('X-Refresh-Attempted');
          if (!alreadyRetried) {
            return this.authService.handleRefreshToken(token!).pipe(
              switchMap((newToken: string) => {
                this.authService.setAccessToken(newToken);
                const retryReq = request.clone({
                  headers: request.headers
                    .set('Authorization', `Bearer ${newToken}`)
                    .set('X-Refresh-Attempted', 'true'),
                });
                return next.handle(retryReq);
              }),
              catchError((err) => this.handleRefreshFailure(err)),
            );
          }
          // El reintento ya se hizo y el recurso sigue devolviendo 401: la
          // credencial no sirve, así que cerramos sesión.
          this.endSession();
          return throwError(() => error);
        }

        // Un 403 significa que la sesión es válida pero el recurso no está
        // permitido: se avisa al usuario y se deja que cada vista decida a
        // dónde ir. El interceptor no navega, no conoce el dominio.
        // Las peticiones complementarias se marcan como silenciosas: su fallo
        // no impide que la pantalla se muestre, así que avisar confundiría.
        if (
          error instanceof HttpErrorResponse &&
          error.status === 403 &&
          !request.headers.has(SILENT_FORBIDDEN_HEADER)
        ) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Sin acceso',
            detail: 'No tienes acceso a este recurso.',
          });
          return throwError(() => error);
        }

        return throwError(() => error);
      }),
    );
  }

  /**
   * Decide qué hacer cuando falla la renovación del token.
   *
   * Solo un fallo que invalida la credencial (401, 403, 400 o un rechazo
   * explícito del backend) justifica expulsar al usuario. Un corte de red o un
   * 5xx temporal del servicio de autenticación son transitorios: la sesión se
   * conserva, se avisa del problema de conexión y el error se propaga para que
   * cada vista decida cómo degradarse.
   *
   * @param error Error emitido por `handleRefreshToken`
   * @returns Observable que siempre re-emite el error recibido
   */
  private handleRefreshFailure(error: unknown): Observable<never> {
    const isTransient =
      error instanceof RefreshTokenError ? error.isTransient : false;

    if (isTransient) {
      this.notifyConnectionProblem();
      return throwError(() => error);
    }

    this.endSession();
    return throwError(() => error);
  }

  /**
   * Cierra la sesión local y lleva al usuario a la pantalla de login.
   *
   * Usa `clearSession()` y no `logout()` de forma deliberada: este último
   * devuelve un `Observable` frío que, sin suscripción, no ejecuta nada y
   * dejaría los tokens vivos en `localStorage`.
   *
   * En el visor público no hace nada: esa pantalla se sirve sin sesión, así que
   * ni hay credencial que invalidar ni pantalla protegida de la que expulsar.
   * Además, `localStorage` es compartido entre pestañas del mismo origen:
   * cerrar la sesión desde el visor tumbaría la del usuario en la pestaña donde
   * sí está trabajando.
   *
   * Antes de expulsar guarda la pantalla en la que estaba el usuario, para
   * devolverlo a ella tras volver a autenticarse. Esta es la ruta que recorre
   * de verdad un deep link abierto tras días sin entrar: el perfil y el refresh
   * token siguen en `localStorage`, así que el `authGuard` deja pasar y la
   * sesión no se descubre caducada hasta que falla esta petición.
   * Ver `specs/spec-047-return-url-deep-links-autenticados.md`.
   */
  private endSession(): void {
    if (enVisorPublico()) {
      return;
    }

    guardarRutaDestino(this.router.url);

    this.authService.clearSession();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Avisa de un problema de conexión al renovar la sesión, respetando la
   * ventana de enfriamiento para no repetir el mensaje en ráfaga.
   */
  private notifyConnectionProblem(): void {
    const now = Date.now();
    if (now - this.lastConnectionToastAt < CONNECTION_TOAST_COOLDOWN_MS) {
      return;
    }
    this.lastConnectionToastAt = now;

    this.messageService.add({
      severity: 'warn',
      summary: 'Problema de conexión',
      detail:
        'No se pudo renovar tu sesión. Verifica tu conexión; se reintentará automáticamente.',
    });
  }

  /**
   * Revisa si la petición actual coincide con una de las URLs excluidas.
   *
   * Solo se evalúa para peticiones que ya pasaron por
   * {@link isProtectedApiRequest}, es decir, dirigidas a una API del sistema.
   * Por eso aquí no hacen falta exclusiones para destinos externos como S3:
   * esas peticiones ni siquiera llegan a este punto.
   *
   * @param request Petición HTTP
   */
  private isExcludedUrl(request: HttpRequest<any>): boolean {
    const isExcluded = EXCLUDED_URLS.some((excluded) =>
      request.url.includes(excluded),
    );

    return isExcluded;
  }

  /**
   * Revisa si la petición actual es para el visor público que requiere token de prueba.
   * @param request Petición HTTP
   */
  private isPublicViewerUrl(request: HttpRequest<any>): boolean {
    const isPublicViewer = PUBLIC_VIEWER_URLS.some((publicUrl) =>
      request.url.includes(publicUrl),
    );

    return isPublicViewer;
  }

  /**
   * Determina si la petición apunta a un backend del sistema que sí debe pasar
   * por las reglas de autenticación del interceptor.
   * Los recursos estáticos del frontend y URLs de terceros deben ignorarse.
   *
   * `eFirmaApiUrl` se incluye porque el servicio de e.firma exige el JWT en el
   * encabezado `Authorization` y valida el claim `exp`; pasar por aquí garantiza
   * además que el token se refresque antes de enviarlo.
   * @param request Petición HTTP
   * @returns true si la URL corresponde a una API del sistema
   */
  private isProtectedApiRequest(request: HttpRequest<any>): boolean {
    const normalizedUrl = request.url.toLowerCase();
    const apiUrl = environment.apiUrl.toLowerCase();
    const apiAuthUrl = environment.apiAuthUrl.toLowerCase();
    const eFirmaApiUrl = environment.eFirmaApiUrl.toLowerCase();

    return (
      normalizedUrl.startsWith(apiUrl) ||
      normalizedUrl.startsWith(apiAuthUrl) ||
      normalizedUrl.startsWith(eFirmaApiUrl)
    );
  }
}
